import { defineStore } from 'pinia'

interface IAppStore {
  themeMode: 'light' | 'dark'
  sidebarExpand: boolean
  isMobileView: boolean
  wrapperWidth: number
  wrapperLeftOffset: number
  navWidth: string
}

const LIGHT = 'light'
const DARK = 'dark'
const THEME_KEY = 'themeMode'
const EXPAND = 280
const COLLAPSED = 72

export const useAppStore = defineStore('app', {
  state: () =>
    <IAppStore>{
      themeMode: LIGHT,
      sidebarExpand: true,
      isMobileView: window.innerWidth < 1024,
      wrapperWidth: EXPAND,
      wrapperLeftOffset: EXPAND,
      navWidth: `calc(100% - ${EXPAND}px)`
    },
  getters: {
    theme: (state) => state.themeMode,
    isDark: (state) => state.themeMode === DARK,
    sidebarExpanded: (state) => state.sidebarExpand,
    isMobile: (state) => state.isMobileView
  },
  actions: {
    toggleSidebar() {
      this.sidebarExpand = !this.sidebarExpand
      this.initWrapper()
    },
    initWrapper(isMobileOverride?: boolean) {
      const isMobile = isMobileOverride ?? window.innerWidth < 1024
      this.isMobileView = isMobile

      if (isMobile) {
        // On mobile, sidebar is hidden by default (overlay)
        this.wrapperWidth = 0
        this.wrapperLeftOffset = 0
        this.navWidth = '100%'
      } else {
        // On desktop, sidebar is visible
        if (this.sidebarExpand) {
          this.wrapperWidth = EXPAND
          this.wrapperLeftOffset = EXPAND
        } else {
          this.wrapperWidth = COLLAPSED
          this.wrapperLeftOffset = COLLAPSED
        }
        this.navWidth = `calc(100% - ${this.wrapperLeftOffset}px)`
      }
    },
    initTheme() {
      const cache = localStorage.getItem(THEME_KEY)
      if (cache) {
        this.themeMode = cache as 'light' | 'dark'
      }
      this.applyTheme()
      this.initWrapper()
      window.addEventListener('resize', this.handleResize.bind(this))
    },
    handleResize() {
      const isMobile = window.innerWidth < 1024

      // When switching from mobile to desktop, expand the sidebar by default.
      if (!isMobile && this.isMobileView) {
        this.sidebarExpand = true
      }

      this.initWrapper(isMobile)
    },
    toggleTheme(event?: MouseEvent) {
      const doToggle = () => {
        this.themeMode = this.themeMode === LIGHT ? DARK : LIGHT
        this.applyTheme()
        localStorage.setItem(THEME_KEY, this.themeMode)
      }

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      if (!event || prefersReducedMotion || !('startViewTransition' in document)) {
        doToggle()
        return
      }

      const x = event.clientX
      const y = event.clientY
      const endRadius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
      )

      const root = document.documentElement
      root.style.setProperty('--theme-x', `${x}px`)
      root.style.setProperty('--theme-y', `${y}px`)
      root.style.setProperty('--theme-radius', `${endRadius}px`)

      const transition = (
        document as Document & {
          startViewTransition: (cb: () => void) => {
            ready: Promise<void>
            finished: Promise<void>
          }
        }
      ).startViewTransition(() => doToggle())

      transition.ready
        .then(() => {
          root.animate(
            {
              clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${endRadius}px at ${x}px ${y}px)`]
            },
            {
              duration: 850,
              easing: 'ease-in-out',
              pseudoElement: '::view-transition-new(root)'
            }
          )
        })
        .catch(() => {})

      transition.finished
        .catch(() => {})
        .finally(() => {
          root.style.removeProperty('--theme-x')
          root.style.removeProperty('--theme-y')
          root.style.removeProperty('--theme-radius')
        })
    },
    appUnmount() {
      window.removeEventListener('resize', this.handleResize.bind(this))
    },
    applyTheme() {
      document.documentElement.classList.remove(LIGHT, DARK)
      document.body.classList.remove(LIGHT, DARK)
      document.documentElement.classList.add(this.themeMode)
      document.body.classList.add(this.themeMode)

      const themeColorMeta = document.querySelector('meta[name="theme-color"]')
      const backgroundValue = getComputedStyle(document.documentElement)
        .getPropertyValue('--background')
        .trim()

      if (themeColorMeta && backgroundValue) {
        themeColorMeta.setAttribute('content', backgroundValue)
      }
    }
  }
})
